import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n/i18n";

export function useRegistrationStatus() {
  const [s, setS] = useState(undefined);
  useEffect(() => { api.get("/registration/status").then((r) => setS(r.data)).catch(() => setS(null)); }, []);
  return s;
}

/** Configurable "registrations closed" page (text, next opening and contact come from the edition). */
export const RegistrationClosed = ({ s }) => {
  const { t, lang } = useI18n();
  return (
    <div className="min-h-screen bg-slate-50" data-testid="registration-closed-page">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-5 py-20">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{s.editionTitle}</p>
        <h1 className="mt-3 font-display text-4xl font-bold tracking-tight" data-testid="registration-closed-text">{s.closedText}</h1>
        {s.nextOpening && <p className="mt-6 text-lg text-slate-700" data-testid="registration-next-opening">{t("Próxima apertura")}: <strong>{new Date(`${s.nextOpening}T12:00:00`).toLocaleDateString(lang)}</strong></p>}
        {s.infoContact && <p className="mt-2 flex items-center gap-2 text-slate-600" data-testid="registration-info-contact"><Info className="h-4 w-4 text-upv" /> {t("Más información")}: {s.infoContact}</p>}
      </main>
      <PageFooter />
    </div>
  );
};

export const ModalityPicker = ({ modalities, value, onChange, error }) => {
  const { t } = useI18n();
  if (!modalities?.length) return null;
  return (
    <div className="mt-6" data-testid="company-reg-modalities">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">{t("Modalidad de participación")}</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {modalities.map((m) => (
          <button key={m.id} type="button" onClick={() => onChange(m.id)} className={`rounded-2xl border p-4 text-left transition-colors ${value === m.id ? "border-upv bg-upv-light" : "border-slate-200 hover:border-slate-400"}`} data-testid={`company-reg-modality-${m.id}`}>
            <span className="flex justify-between gap-2 font-semibold">{m.name}{m.price != null && <span className="text-sm text-slate-600">{m.price} €</span>}</span>
            {m.description && <span className="mt-1 block text-xs text-slate-500">{m.description}</span>}
          </button>
        ))}
      </div>
      {error && <p className="mt-2 text-xs font-medium text-rose-600" data-testid="company-reg-error-modalityId">{t(error)}</p>}
    </div>
  );
};

export const ReturningBanner = ({ info, onUse }) => {
  const { t } = useI18n();
  if (!info?.exists) return null;
  return (
    <div className="mt-4 rounded-2xl border border-upv/30 bg-upv-light/60 p-4 text-sm" data-testid="company-reg-returning">
      <p className="font-semibold">{t("Tu empresa ya ha participado en el Foro")}{info.years?.length ? ` (${info.years.join(", ")})` : ""}.</p>
      <p className="mt-1 text-slate-600">{t("Si ya tienes cuenta, inicia sesión y usa «Inscribir en» desde tu portal para recuperar todos los datos. También puedes cargar aquí los datos públicos y revisarlos.")}</p>
      <button type="button" onClick={onUse} className="mt-3 rounded-xl bg-slate-950 px-4 py-2 text-xs font-bold text-white" data-testid="company-reg-use-previous">{t("Cargar datos anteriores")}</button>
    </div>
  );
};
